import React, { useState, useEffect, useRef } from "react";
import { Navbar, Nav, Container } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faGlobe } from "@fortawesome/free-solid-svg-icons";
import { Link, useLocation } from "react-router-dom";
import "./Navbar.css";
import logo from "../assets/images/anvika-logo.png";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिंदी" },
  { code: "mr", label: "मराठी" },
];

// Inject Google Translate script once
function loadGoogleTranslate() {
  if (document.getElementById("google-translate-script")) return;
  window.googleTranslateElementInit = () => {
    new window.google.translate.TranslateElement(
      { pageLanguage: "en", autoDisplay: false },
      "google_translate_element"
    );
  };
  const script = document.createElement("script");
  script.id = "google-translate-script";
  script.src =
    "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  script.async = true;
  document.body.appendChild(script);
}

function triggerGoogleTranslate(langCode) {
  // Try using the hidden Google Translate combo box
  const trySelect = () => {
    const select = document.querySelector(".goog-te-combo");
    if (select) {
      select.value = langCode;
      select.dispatchEvent(new Event("change"));
      return true;
    }
    return false;
  };

  if (!trySelect()) {
    // Widget not ready yet — wait and retry
    const interval = setInterval(() => {
      if (trySelect()) clearInterval(interval);
    }, 300);
    setTimeout(() => clearInterval(interval), 5000);
  }
}

const NavigationBar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [activeLang, setActiveLang] = useState("en");
  const langRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    loadGoogleTranslate();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLangSelect = (code) => {
    setActiveLang(code);
    setLangOpen(false);
    triggerGoogleTranslate(code);
  };

  return (
    <Navbar
      expand="lg"
      fixed="top"
      className={`custom-navbar ${scrolled ? "scrolled" : ""}`}
      expanded={expanded}
    >
      {/* Hidden Google Translate widget */}
      <div id="google_translate_element" style={{ display: "none" }} />

      <Container>
        <Navbar.Brand as={Link} to="/" className="brand-logo">
          <img
            src={logo}
            alt="Anvika Industrial Supplier Logo"
            className="brand-logo-img"
          />
        </Navbar.Brand>

        <Navbar.Toggle
          aria-controls="basic-navbar-nav"
          onClick={() => setExpanded(!expanded)}
        >
          <FontAwesomeIcon icon={faBars} />
        </Navbar.Toggle>
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="ms-auto align-items-lg-center">
            <Nav.Link
              as={Link}
              to="/"
              onClick={() => setExpanded(false)}
              className={location.pathname === "/" ? "active" : ""}
            >
              Home
            </Nav.Link>
            <Nav.Link
              as={Link}
              to="/products"
              onClick={() => setExpanded(false)}
              className={location.pathname === "/products" ? "active" : ""}
            >
              Products
            </Nav.Link>
            <Nav.Link
              as={Link}
              to="/services"
              onClick={() => setExpanded(false)}
              className={location.pathname === "/services" ? "active" : ""}
            >
              Services
            </Nav.Link>
            <Nav.Link
              as={Link}
              to="/material"
              onClick={() => setExpanded(false)}
              className={location.pathname === "/material" ? "active" : ""}
            >
              Product Categories
            </Nav.Link>
            <Nav.Link
              as={Link}
              to="/about"
              onClick={() => setExpanded(false)}
              className={location.pathname === "/about" ? "active" : ""}
            >
              About Us
            </Nav.Link>
            <Nav.Link
              as={Link}
              to="/contact"
              onClick={() => setExpanded(false)}
              className={location.pathname === "/contact" ? "active" : ""}
            >
              Contact
            </Nav.Link>

            {/* Google Translate Dropdown */}
            <div className="translate-wrapper" ref={langRef}>
              <button
                className="translate-btn"
                onClick={() => setLangOpen(!langOpen)}
                title="Translate page"
              >
                <FontAwesomeIcon icon={faGlobe} />
                <span className="translate-label">
                  {LANGUAGES.find((l) => l.code === activeLang)?.label}
                </span>
              </button>
              {langOpen && (
                <ul className="translate-dropdown">
                  {LANGUAGES.map((lang) => (
                    <li
                      key={lang.code}
                      className={activeLang === lang.code ? "active" : ""}
                      onClick={() => handleLangSelect(lang.code)}
                    >
                      {lang.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default NavigationBar;
